import { useState, useCallback, useRef } from 'react'
import supabase, { getCached, setCached, lsGet, lsSet, invalidateCache } from '../supabase/supabase'

const TABLE = 'site_pages'
const PAGES_CACHE_PREFIX = 'pages_'

function extractBannerUrl(content) {
  if (!content?.droppedElements) return null
  for (const el of content.droppedElements) {
    if (el.type === 'container' && el.styles?.backgroundType === 'image' && el.styles?.backgroundImage)
      return el.styles.backgroundImage
  }
  for (const el of content.droppedElements) {
    if (el.type === 'image' && el.styles?.imageUrl) return el.styles.imageUrl
    if (el.type === 'container' && el.columns) {
      for (const col of el.columns) {
        for (const widget of col) {
          if (widget.type === 'image' && widget.styles?.imageUrl) return widget.styles.imageUrl
        }
      }
    }
  }
  return null
}

async function updateWebsiteBanner(projectId, pageContent) {
  if (!projectId || !pageContent) return
  try {
    const bannerUrl = extractBannerUrl(pageContent)
    await supabase
      .from('websites_1772750000000')
      .update({ banner_url: bannerUrl, updated_at: new Date().toISOString() })
      .eq('id', projectId)
  } catch {}
}

export function usePagesPersistence() {
  const [isSaving, setIsSaving] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const saveQueue = useRef({})
  const saveTimer = useRef(null)
  const bannerUpdateQueue = useRef(new Set())

  const loadPages = useCallback(async (projectId) => {
    if (!projectId) return []

    const cacheKey = `${PAGES_CACHE_PREFIX}${projectId}`

    // 1. In-memory cache
    const memCached = getCached(cacheKey)
    if (memCached) {
      // Background revalidate
      ;(async () => {
        try {
          const { data } = await supabase
            .from(TABLE)
            .select('id,name,slug,sort_order,content,project_id')
            .eq('project_id', projectId)
            .order('sort_order', { ascending: true })
          if (data) { setCached(cacheKey, data); lsSet(cacheKey, data) }
        } catch {}
      })()
      return memCached
    }

    // 2. localStorage cache
    const lsCached = lsGet(cacheKey)
    if (lsCached) {
      setCached(cacheKey, lsCached)
      // Background revalidate
      ;(async () => {
        try {
          const { data } = await supabase
            .from(TABLE)
            .select('id,name,slug,sort_order,content,project_id')
            .eq('project_id', projectId)
            .order('sort_order', { ascending: true })
          if (data) { setCached(cacheKey, data); lsSet(cacheKey, data) }
        } catch {}
      })()
      return lsCached
    }

    // 3. Fresh fetch
    setIsLoading(true)
    try {
      const { data, error } = await supabase
        .from(TABLE)
        .select('id,name,slug,sort_order,content,project_id')
        .eq('project_id', projectId)
        .order('sort_order', { ascending: true })
      if (error) throw error
      const pages = data || []
      setCached(cacheKey, pages)
      lsSet(cacheKey, pages)
      return pages
    } catch (err) {
      console.error('Failed to load pages:', err)
      return []
    } finally {
      setIsLoading(false)
    }
  }, [])

  const savePage = useCallback(async (projectId, page) => {
    if (!projectId) return
    try {
      const { error } = await supabase
        .from(TABLE)
        .upsert(
          { id: page.id, project_id: projectId, name: page.name, slug: page.slug, sort_order: page.sort_order ?? 0, content: page.content ?? {}, updated_at: new Date().toISOString() },
          { onConflict: 'id' }
        )
      if (error) throw error

      // Update in-memory & ls cache
      const cacheKey = `${PAGES_CACHE_PREFIX}${projectId}`
      const existing = getCached(cacheKey) || lsGet(cacheKey) || []
      const idx = existing.findIndex(p => p.id === page.id)
      const updated = idx >= 0
        ? existing.map((p, i) => i === idx ? { ...p, ...page } : p)
        : [...existing, page]
      setCached(cacheKey, updated)
      lsSet(cacheKey, updated)

      if (page.sort_order === 0 || page.slug === '/' || page.name?.toLowerCase() === 'home') {
        updateWebsiteBanner(projectId, page.content).catch(() => {})
      }
    } catch (err) {
      console.error('Failed to save page:', err)
    }
  }, [])

  const savePageContent = useCallback((projectId, pageId, content) => {
    if (!projectId) return
    saveQueue.current[pageId] = content
    bannerUpdateQueue.current.add(projectId)

    if (saveTimer.current) clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(async () => {
      const queue = { ...saveQueue.current }
      const projectsToUpdate = new Set(bannerUpdateQueue.current)
      saveQueue.current = {}
      bannerUpdateQueue.current.clear()

      setIsSaving(true)
      try {
        await Promise.all(
          Object.entries(queue).map(([id, c]) =>
            supabase.from(TABLE)
              .update({ content: c, updated_at: new Date().toISOString() })
              .eq('id', id)
              .eq('project_id', projectId)
          )
        )

        // Update page cache entries
        const cacheKey = `${PAGES_CACHE_PREFIX}${projectId}`
        const existing = getCached(cacheKey) || lsGet(cacheKey) || []
        const updated = existing.map(p => queue[p.id] ? { ...p, content: queue[p.id] } : p)
        setCached(cacheKey, updated)
        lsSet(cacheKey, updated)

        for (const pid of projectsToUpdate) {
          const { data: homePage } = await supabase
            .from(TABLE)
            .select('content')
            .eq('project_id', pid)
            .or('sort_order.eq.0,slug.eq./')
            .order('sort_order', { ascending: true })
            .limit(1)
            .single()
          if (homePage?.content) updateWebsiteBanner(pid, homePage.content).catch(() => {})
        }
      } catch (err) {
        console.error('Failed to save page content:', err)
      } finally {
        setIsSaving(false)
      }
    }, 1500)
  }, [])

  const savePageContentImmediate = useCallback(async (projectId, pageId, content) => {
    if (!projectId) return { success: false, error: 'No project ID provided.' }
    try {
      const { error } = await supabase
        .from(TABLE)
        .update({ content, updated_at: new Date().toISOString() })
        .eq('id', pageId)
        .eq('project_id', projectId)
      if (error) throw error

      // Clear from debounce queue
      if (saveQueue.current[pageId]) delete saveQueue.current[pageId]

      // Update cache
      const cacheKey = `${PAGES_CACHE_PREFIX}${projectId}`
      const existing = getCached(cacheKey) || lsGet(cacheKey) || []
      const updated = existing.map(p => p.id === pageId ? { ...p, content } : p)
      setCached(cacheKey, updated)
      lsSet(cacheKey, updated)

      updateWebsiteBanner(projectId, content).catch(() => {})
      return { success: true }
    } catch (err) {
      console.error('Failed to save page content immediately:', err)
      return { success: false, error: err.message || 'Network or database error.' }
    }
  }, [])

  const deletePage = useCallback(async (projectId, pageId) => {
    if (!projectId) return
    try {
      const { error } = await supabase
        .from(TABLE)
        .delete()
        .eq('id', pageId)
        .eq('project_id', projectId)
      if (error) throw error

      // Remove from cache
      const cacheKey = `${PAGES_CACHE_PREFIX}${projectId}`
      const existing = getCached(cacheKey) || lsGet(cacheKey) || []
      const updated = existing.filter(p => p.id !== pageId)
      setCached(cacheKey, updated)
      lsSet(cacheKey, updated)
    } catch (err) {
      console.error('Failed to delete page:', err)
    }
  }, [])

  const updatePageMeta = useCallback(async (projectId, pageId, updates) => {
    if (!projectId) return
    try {
      const { error } = await supabase
        .from(TABLE)
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', pageId)
        .eq('project_id', projectId)
      if (error) throw error

      // Update cache
      const cacheKey = `${PAGES_CACHE_PREFIX}${projectId}`
      const existing = getCached(cacheKey) || lsGet(cacheKey) || []
      const updated = existing.map(p => p.id === pageId ? { ...p, ...updates } : p)
      setCached(cacheKey, updated)
      lsSet(cacheKey, updated)
    } catch (err) {
      console.error('Failed to update page meta:', err)
    }
  }, [])

  return { loadPages, savePage, savePageContent, savePageContentImmediate, deletePage, updatePageMeta, isSaving, isLoading }
}